import api from './api'

export type ScheduleRules = {
  skipSundays: boolean
  minFreeDaysBetweenPapers: number
  holidays: Array<{ date: string; name: string }>
}

export type BuilderPaper = {
  id: string
  className: string
  subjectId: string
  subjectName: string
  subjectCode: string
  date: string | null
  clash?: boolean
}

export type DayRow = {
  date: string
  kind: 'open' | 'sunday' | 'holiday'
  label: string | null
}

export type DatesheetBuilderPayload = {
  exam: {
    _id: string
    name: string
    code: string
    scheduleStartDate: string | null
    scheduleEndDate: string | null
    sittingStart: string
    sittingEnd: string
    scheduleStatus: 'draft' | 'published'
    schedulePublishedAt: string | null
  }
  scheduleRules: ScheduleRules
  papers: BuilderPaper[]
  clashes: Array<Record<string, unknown>>
  stats: {
    totalPapers: number
    scheduledCount: number
    unscheduledCount: number
    examDays: number
    clashCount: number
  }
  dayRows: DayRow[]
  classes: string[]
}

const unwrap = (response: { data?: { data?: DatesheetBuilderPayload } }) =>
  response?.data?.data as DatesheetBuilderPayload

const get = async (examId: string): Promise<DatesheetBuilderPayload> => {
  const response = await api.get(`/exam-datesheets/${examId}`)
  return unwrap(response)
}

const putRules = async (
  examId: string,
  body: Partial<{
    scheduleRules: ScheduleRules
    scheduleStartDate: string
    scheduleEndDate: string
    sittingStart: string
    sittingEnd: string
  }>
): Promise<DatesheetBuilderPayload> => {
  const response = await api.put(`/exam-datesheets/${examId}/rules`, body)
  return unwrap(response)
}

const patchPaper = async (examId: string, paperId: string, date: string | null): Promise<DatesheetBuilderPayload> => {
  const response = await api.patch(`/exam-datesheets/${examId}/papers/${paperId}`, { date })
  return unwrap(response)
}

const autoArrange = async (
  examId: string
): Promise<DatesheetBuilderPayload & { unplacedCount?: number; previousSnapshot?: Array<{ id: string; date: string | null }> }> => {
  const response = await api.post(`/exam-datesheets/${examId}/auto-arrange`)
  return response?.data?.data
}

const publish = async (examId: string): Promise<DatesheetBuilderPayload> => {
  const response = await api.post(`/exam-datesheets/${examId}/publish`)
  return unwrap(response)
}

const syncPapers = async (examId: string): Promise<DatesheetBuilderPayload> => {
  const response = await api.post(`/exam-datesheets/${examId}/papers/sync`)
  return unwrap(response)
}

const suggestHolidays = async (examId: string): Promise<Array<{ date: string; name: string }>> => {
  const response = await api.get(`/exam-datesheets/${examId}/holidays/suggest`)
  return response?.data?.data || []
}

export default {
  get,
  putRules,
  patchPaper,
  autoArrange,
  publish,
  syncPapers,
  suggestHolidays,
}
